/**
 * AbschlussSection - the Tab-6 «Policenkontrolle und Abschluss» section (P2 Placement Slice 15): the
 * linked police, the aktuell Kontrolle (+ earlier ones), the closure state and the Zustellung, plus the
 * four server-driven actions (Kontrolle ausfuehren, Police verknuepfen, Abweichung geklaert, Per E-Mail
 * zustellen). The server owns every gate via `aktionen`; the UI only renders + triggers.
 */
import { useState } from 'react';
import {
  PK_KEINE_POLICE_HINWEIS,
  PK_ERLEDIGT_HINWEIS,
  PK_UNVOLLSTAENDIG_HINWEIS,
  PK_WARTET_AKTIVIERUNG_HINWEIS,
  PK_WARTET_KLAERUNG_HINWEIS,
  PK_ZUSTELLUNG_MANUELL_HINWEIS,
  PK_ZUSTELLUNG_PORTAL_HINWEIS,
  PK_ZUSTELLUNG_EMAIL_HINWEIS,
  PK_ZUSTELLUNG_FEHLER_HINWEIS,
  PK_ZUSTELLUNG_UNTERBROCHEN_HINWEIS,
  buildPkRueckmeldungText,
  type PolicenkontrolleEintrag,
  type PlacementAbschlussResponse,
} from '@brokly/shared';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { dateShortDe, dateTimeShortDe } from '../../lib/format';
import { PlacementClosureBadge } from './PlacementClosureBadge';
import { PolicenkontrolleTabelle } from './PolicenkontrolleTabelle';
import { PkPoliceVerknuepfenDialog } from './PkPoliceVerknuepfenDialog';
import { PkKlaerenDialog } from './PkKlaerenDialog';
import { PkZustellenDialog } from './PkZustellenDialog';
import { PkRueckmeldungDialog } from './PkRueckmeldungDialog';
import {
  usePlacementAbschluss,
  useKontrolleAbschluss,
  usePoliceVerknuepfenAbschluss,
  useAbweichungGeklaertAbschluss,
  usePoliceZustellenAbschluss,
  useZustellungAufloesenAbschluss,
  useRueckmeldungVersichererAbschluss,
} from './usePlacementAbschluss';

function KontrolleCard({ k }: { k: PolicenkontrolleEintrag }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant={k.result === 'deckungsgleich' ? 'success' : 'danger'}>
          {k.result === 'deckungsgleich' ? 'Deckungsgleich' : 'Abweichung'}
        </Badge>
        <span className="text-xs text-muted-foreground">
          Kontrolle {k.kontroll_nr} am {dateTimeShortDe(k.ausgefuehrt_at)} - {k.ausgeloest_durch_label}, verknuepft via {k.match_art_label}
        </span>
      </div>
      {/* P2 hardening 05: lead with the human Befund (the concrete per-field difference), then the table. */}
      <p className="text-sm font-medium">{k.befund.headline}</p>
      {k.befund.abweichungen.length > 0 && (
        <ul className="space-y-0.5">
          {k.befund.abweichungen.map((line, i) => (
            <li key={i} className="text-sm text-status-danger" data-testid="pk-befund-abweichung">{line}</li>
          ))}
        </ul>
      )}
      {k.befund.nicht_pruefbar.length > 0 && (
        <ul className="space-y-0.5">
          {k.befund.nicht_pruefbar.map((line, i) => (
            <li key={i} className="text-xs text-muted-foreground">{line}</li>
          ))}
        </ul>
      )}
      <PolicenkontrolleTabelle zeilen={k.zeilen} />
      {k.klaerung.status === 'geklaert' && (
        <p className="text-xs text-muted-foreground">Geklaert{k.klaerung.geklaert_by_name ? ` von ${k.klaerung.geklaert_by_name}` : ''}: {k.klaerung.grund}</p>
      )}
      {k.rueckmeldung && (
        <p className="text-xs text-muted-foreground" data-testid="pk-rueckmeldung-line">
          Rueckmeldung gesendet am {dateTimeShortDe(k.rueckmeldung.gesendet_at)} an {k.rueckmeldung.empfaenger}
          {k.rueckmeldung.gesendet_by_name ? ` (${k.rueckmeldung.gesendet_by_name})` : ''}
        </p>
      )}
      {!k.vollstaendig && <p className="text-xs text-status-warning">{PK_UNVOLLSTAENDIG_HINWEIS}</p>}
    </div>
  );
}

/** The default Rueckmeldung body prefilled from the aktuell Kontrolle (edited in the dialog). */
function defaultRueckmeldungText(data: PlacementAbschlussResponse): string {
  if (!data.kontrolle) return '';
  return buildPkRueckmeldungText({
    versicherer_name: data.antrag?.carrier_name ?? data.police?.carrier_name ?? 'Versicherer',
    kunde_name: 'den Kunden',
    policy_number: data.police?.policy_number ?? data.antrag?.police_erhalten_policy_number ?? '',
    submission_number: data.antrag?.submission_number ?? null,
    befund: data.kontrolle.befund,
  });
}

export function AbschlussSection({ placementId, canWrite, canSend }: { placementId: string; canWrite: boolean; canSend: boolean }) {
  const { data } = usePlacementAbschluss(placementId);
  const kontrolle = useKontrolleAbschluss(placementId);
  const verknuepfen = usePoliceVerknuepfenAbschluss(placementId);
  const klaeren = useAbweichungGeklaertAbschluss(placementId);
  const zustellen = usePoliceZustellenAbschluss(placementId);
  const zustellungAufloesen = useZustellungAufloesenAbschluss(placementId);
  const rueckmelden = useRueckmeldungVersichererAbschluss(placementId);

  const [verknuepfenOpen, setVerknuepfenOpen] = useState(false);
  const [klaerenOpen, setKlaerenOpen] = useState(false);
  const [zustellenOpen, setZustellenOpen] = useState(false);
  const [rueckmeldenOpen, setRueckmeldenOpen] = useState(false);

  if (!data) return null;
  const a = data.aktionen;
  const frueher = data.kontrollen.filter((k) => !k.is_aktuell);

  return (
    <section className="space-y-4" data-testid="placement-abschluss-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold">Policenkontrolle und Abschluss</h3>
        <PlacementClosureBadge
          status={data.closure.status}
          startZeitpunkt={data.closure.start_zeitpunkt}
          endZeitpunkt={data.closure.end_zeitpunkt}
          abschlussArt={data.closure.abschluss_art}
        />
      </div>

      {canWrite && (
        <div className="flex flex-wrap gap-2">
          {a.kontrolle_ausfuehren && (
            <Button size="sm" variant="secondary" disabled={kontrolle.isPending} onClick={() => kontrolle.mutate()} data-testid="pk-action-kontrolle">
              Policenkontrolle ausfuehren
            </Button>
          )}
          {a.police_verknuepfen && (
            <Button size="sm" variant="secondary" onClick={() => setVerknuepfenOpen(true)} data-testid="pk-action-verknuepfen">
              Police verknuepfen
            </Button>
          )}
          {a.rueckmeldung_versicherer && (
            <Button size="sm" variant="secondary" disabled={!canSend} title={!canSend ? 'Keine Berechtigung fuer den E-Mail-Versand' : undefined} onClick={() => setRueckmeldenOpen(true)} data-testid="pk-action-rueckmeldung">
              Rueckmeldung an Versicherer
            </Button>
          )}
          {a.abweichung_klaeren && (
            <Button size="sm" variant="destructive" onClick={() => setKlaerenOpen(true)} data-testid="pk-action-klaeren">
              Abweichung geklaert
            </Button>
          )}
          {a.police_zustellen && (
            <Button size="sm" variant="secondary" disabled={!canSend} title={!canSend ? 'Keine Berechtigung fuer den E-Mail-Versand' : undefined} onClick={() => setZustellenOpen(true)} data-testid="pk-action-zustellen">
              Per E-Mail zustellen
            </Button>
          )}
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-sm">Police</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-sm">
          {data.police ? (
            <>
              <div className="flex items-center gap-2">
                <span className="font-medium">{data.police.policy_number}</span>
                <Badge variant={data.police.aktiv ? 'success' : 'neutral'}>{data.police.status_label}</Badge>
              </div>
              <div className="text-xs text-muted-foreground">
                {data.police.carrier_name ?? data.police.carrier_uid ?? '-'} · Beginn {data.police.inception_date ? dateShortDe(data.police.inception_date) : '-'} · Ablauf {data.police.expiry_date ? dateShortDe(data.police.expiry_date) : '-'}
                {data.police.premium_gross != null ? ` · CHF ${data.police.premium_gross}` : ''}
              </div>
              {data.police.ersetzt_shell_id && <p className="text-xs text-muted-foreground">Entwurf-Police abgeloest.</p>}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">{PK_KEINE_POLICE_HINWEIS}</p>
          )}
        </CardContent>
      </Card>

      {data.kontrolle && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Kontrolle</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <KontrolleCard k={data.kontrolle} />
            {frueher.length > 0 && (
              <details>
                <summary className="cursor-pointer text-xs text-muted-foreground">Fruehere Kontrollen ({frueher.length})</summary>
                <div className="mt-2 space-y-3">{frueher.map((k) => <KontrolleCard key={k.id} k={k} />)}</div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-sm">Abschluss</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-sm">
          {data.closure.abschluss_art_label && <p>{data.closure.abschluss_art_label}</p>}
          <p className="text-xs text-muted-foreground">
            Start {dateTimeShortDe(data.closure.start_zeitpunkt)}
            {data.closure.end_zeitpunkt ? ` · Ende ${dateTimeShortDe(data.closure.end_zeitpunkt)}` : ''}
            {data.closure.dauer_tage != null ? ` · ${data.closure.dauer_tage} Tage` : ''}
          </p>
          {data.closure.status === 'erledigt' ? (
            <p className="text-xs text-muted-foreground">{PK_ERLEDIGT_HINWEIS}</p>
          ) : data.police?.status === 'draft' ? (
            <p className="text-xs text-muted-foreground">{PK_WARTET_AKTIVIERUNG_HINWEIS}</p>
          ) : data.kontrolle?.klaerung.status === 'offen' ? (
            <p className="text-xs text-muted-foreground">{PK_WARTET_KLAERUNG_HINWEIS}</p>
          ) : null}
        </CardContent>
      </Card>

      {data.closure.status === 'erledigt' && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Zustellung</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            {data.zustellung.channel === 'portal' && <p className="text-xs text-muted-foreground">{PK_ZUSTELLUNG_PORTAL_HINWEIS}</p>}
            {data.zustellung.channel === 'email' && <p className="text-xs text-muted-foreground">{PK_ZUSTELLUNG_EMAIL_HINWEIS}</p>}
            {!data.zustellung.zugestellt_at && !data.zustellung.portal_verfuegbar && <p className="text-xs text-muted-foreground">{PK_ZUSTELLUNG_MANUELL_HINWEIS}</p>}
            {data.zustellung.channel_label && data.zustellung.zugestellt_at && (
              <p className="text-xs text-muted-foreground">{data.zustellung.channel_label} · {dateTimeShortDe(data.zustellung.zugestellt_at)}</p>
            )}
            {data.zustellung.letzter_fehler && !data.zustellung.versand_gesperrt && <p className="text-xs text-status-danger">{PK_ZUSTELLUNG_FEHLER_HINWEIS}</p>}
            {data.zustellung.versand_unterbrochen && !data.zustellung.versand_gesperrt && (
              <p className="text-xs text-muted-foreground">Letzter Versand unterbrochen, erneut versuchen moeglich.</p>
            )}
            {data.zustellung.versand_gesperrt && (
              <div className="space-y-1" data-testid="pk-zustellung-gesperrt">
                <p className="text-xs text-status-danger">{PK_ZUSTELLUNG_UNTERBROCHEN_HINWEIS}</p>
                <div className="flex gap-2">
                  {data.zustellung.nachtragen_moeglich && (
                    <Button size="sm" variant="secondary" disabled={zustellungAufloesen.isPending} onClick={() => zustellungAufloesen.mutate('nachtragen')} data-testid="pk-zustellung-nachtragen">
                      Als versandt erfassen
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" disabled={zustellungAufloesen.isPending} onClick={() => zustellungAufloesen.mutate('freigeben')} data-testid="pk-zustellung-freigeben">
                    Erneuten Versand freigeben
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <PkPoliceVerknuepfenDialog
        open={verknuepfenOpen}
        onClose={() => setVerknuepfenOpen(false)}
        saving={verknuepfen.isPending}
        error={verknuepfen.error as { code?: string } | null}
        onVerknuepfen={(policy_number) => verknuepfen.mutate({ policy_number }, { onSuccess: () => setVerknuepfenOpen(false) })}
      />
      <PkKlaerenDialog
        open={klaerenOpen}
        onClose={() => setKlaerenOpen(false)}
        saving={klaeren.isPending}
        error={klaeren.error as { code?: string } | null}
        onKlaeren={(grund) => klaeren.mutate({ grund }, { onSuccess: () => setKlaerenOpen(false) })}
      />
      <PkZustellenDialog
        open={zustellenOpen}
        onClose={() => setZustellenOpen(false)}
        kundeEmail={data.zustellung.kunde_email}
        saving={zustellen.isPending}
        error={zustellen.error as { code?: string } | null}
        onZustellen={() => zustellen.mutate(undefined, { onSuccess: () => setZustellenOpen(false) })}
      />
      <PkRueckmeldungDialog
        open={rueckmeldenOpen}
        onClose={() => setRueckmeldenOpen(false)}
        saving={rueckmelden.isPending}
        error={rueckmelden.error as { code?: string } | null}
        defaultTo={data.rueckmeldung_empfaenger_vorschlag}
        defaultText={defaultRueckmeldungText(data)}
        bereitsGesendet={!!data.kontrolle?.rueckmeldung}
        onSend={({ to, text }) => rueckmelden.mutate({ to, text }, { onSuccess: () => setRueckmeldenOpen(false) })}
      />
    </section>
  );
}
